import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';

// Backoffice Calendar View for Abschlussgespräche
const BackofficeExamsCalendar = ({ appointments, handleResetReschedule, resettingReschedule }) => {
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [leadMeetings, setLeadMeetings] = useState([]);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/leads/my-zoom-meetings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setLeadMeetings(r.data || []))
      .catch(() => {});
  }, []); // fetch once on mount

  const statusBadge = (status) => {
    const cfg = {
      confirmed: { color: 'bg-green-100 text-green-800', label: 'Bestätigt' },
      pending:   { color: 'bg-yellow-100 text-yellow-800', label: 'Ausstehend' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Abgesagt' },
      canceled:  { color: 'bg-red-100 text-red-800', label: 'Abgesagt' },
      completed: { color: 'bg-blue-100 text-blue-800', label: 'Abgeschlossen' },
      scheduled: { color: 'bg-blue-100 text-blue-800', label: 'Geplant' },
    };
    const c = cfg[status] || { color: 'bg-gray-100 text-gray-500', label: status || 'Geplant' };
    return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${c.color}`}>{c.label}</span>;
  };

  const appointmentsByDate = useMemo(() => {
    const map = {};
    for (const appt of appointments) {
      if (!appt.date) continue;
      const d = new Date(appt.date);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (!map[key]) map[key] = [];
      map[key].push({ ...appt, _type: 'appointment' });
    }
    // Merge lead zoom meetings
    for (const lead of leadMeetings) {
      const zm = lead.zoom_meeting;
      if (!zm?.date) continue;
      const d = new Date(zm.date + 'T12:00:00');
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (!map[key]) map[key] = [];
      map[key].push({
        ...lead,
        _type: 'lead',
        date: `${zm.date}T${zm.time || '00:00'}:00`,
        status: zm.status === 'cancelled' ? 'cancelled' : 'confirmed',
        zoom_link: zm.link,
        person: zm.person,
        name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
      });
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => new Date(a.date) - new Date(b.date));
    }
    return map;
  }, [appointments, leadMeetings]);

  const calendarKey = `${calendarDate.getFullYear()}-${String(calendarDate.getMonth()+1).padStart(2,'0')}-${String(calendarDate.getDate()).padStart(2,'0')}`;
  const selectedDayAppointments = appointmentsByDate[calendarKey] || [];

  const getDayClassName = (date) => {
    const key = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
    const items = appointmentsByDate[key];
    if (!items || items.length === 0) return '';
    const hasLead = items.some(a => a._type === 'lead' && a.status !== 'cancelled');
    const hasAppt = items.some(a => a._type === 'appointment' && a.status !== 'cancelled');
    const allCancelled = items.every(a => a.status === 'cancelled');
    if (allCancelled) return 'all-cancelled-date';
    if (hasLead && hasAppt) return 'mixed-date';
    if (hasLead) return 'lead-date';
    return 'available-date';
  };

  const upcoming = appointments.filter(a => a.date && new Date(a.date) > new Date());
  const stats = {
    total: appointments.length,
    upcoming: upcoming.length,
    confirmed: appointments.filter(a => a.status === 'confirmed').length,
    cancelled: appointments.filter(a => a.status === 'cancelled' || a.status === 'canceled').length,
  };

  if (appointments.length === 0) return (
    <div className="bg-white rounded-xl shadow-lg p-8 text-center">
      <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="18" rx="2" strokeWidth={1.5} /><line x1="16" y1="2" x2="16" y2="6" strokeWidth={1.5} /><line x1="8" y1="2" x2="8" y2="6" strokeWidth={1.5} /><line x1="3" y1="10" x2="21" y2="10" strokeWidth={1.5} />
      </svg>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">Keine Abschlussgespräche</h3>
      <p className="text-gray-600">Es sind noch keine Termine vorhanden.</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
          <p className="text-xs text-gray-500 mt-1">Gesamt</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{stats.upcoming}</p>
          <p className="text-xs text-gray-500 mt-1">Anstehend</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{stats.confirmed}</p>
          <p className="text-xs text-gray-500 mt-1">Bestätigt</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-red-500">{stats.cancelled}</p>
          <p className="text-xs text-gray-500 mt-1">Abgesagt</p>
        </div>
      </div>

      {/* Calendar + Day Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6" data-testid="backoffice-calendar">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Terminkalender
          </h2>
          <DatePicker
            selected={calendarDate}
            onChange={(date) => setCalendarDate(date)}
            locale="de"
            inline
            dayClassName={getDayClassName}
            calendarClassName="modern-calendar"
            showPopperArrow={false}
          />
          <div className="mt-3 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>
              Abschlussgespräch
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-purple-500 inline-block"></span>
              Lead-Gespräch
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-red-400 inline-block"></span>
              Alle abgesagt
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6" data-testid="backoffice-day-detail">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {calendarDate.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </h2>
          {selectedDayAppointments.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <p className="text-sm font-medium">Keine Termine an diesem Tag</p>
            </div>
          ) : (
            <div className="space-y-3">
              {selectedDayAppointments.map(appt => {
                const d = appt.date ? new Date(appt.date) : null;
                const time = d ? d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';
                const isPast = d && d < new Date();
                const isLead = appt._type === 'lead';
                return (
                  <div key={appt.id}
                    className={`border rounded-xl p-4 transition-shadow hover:shadow-md ${
                      isPast
                        ? 'border-gray-200 bg-gray-50 opacity-70'
                        : isLead
                          ? 'border-purple-200 bg-purple-50/40'
                          : 'border-blue-200 bg-blue-50/30'
                    }`}
                    data-testid={`bo-cal-appt-${appt.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-bold text-gray-900">{time} Uhr</span>
                          {isLead ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">Lead-Gespräch</span>
                          ) : (
                            statusBadge(appt.status)
                          )}
                          {isPast && <span className="text-xs text-gray-400 italic">vergangen</span>}
                        </div>
                        {isLead ? (
                          <>
                            <p className="text-sm text-gray-700">
                              Erstgespräch: <span className="font-medium">{appt.name}</span>
                            </p>
                            {appt.person && <p className="text-xs text-gray-400 mt-0.5">Zuständig: {appt.person}</p>}
                            {appt.email && <p className="text-xs text-gray-400">{appt.email}</p>}
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">{appt.vertriebler?.name ?? '—'}</span>
                              <span className="text-gray-400 mx-1">mit</span>
                              <span className="font-medium">{appt.vertriebsleiter?.name ?? '—'}</span>
                            </p>
                            {appt.vertriebler?.email && (
                              <p className="text-xs text-gray-400 mt-0.5">{appt.vertriebler.email}</p>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex gap-2 flex-shrink-0 flex-wrap">
                        {(isLead ? appt.zoom_link : appt.zoom_url) && !isPast && (
                          <a href={isLead ? appt.zoom_link : appt.zoom_url} target="_blank" rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg text-xs font-medium transition-colors ${isLead ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                            </svg>
                            Zoom
                          </a>
                        )}
                      </div>
                    </div>
                    {/* Reschedule info (only for academy appointments) */}
                    {!isLead && appt.vertriebler?.id && (
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                        <p className="text-xs text-gray-400">
                          Verschiebungen: <span className="font-medium text-gray-600">{appt.vertriebler?.appointment_reschedule_count ?? 0} / 2</span>
                        </p>
                        <button
                          onClick={() => handleResetReschedule(appt.vertriebler.id, appt.vertriebler.name)}
                          disabled={resettingReschedule === appt.vertriebler.id}
                          className="text-xs text-orange-600 hover:text-orange-800 font-medium border border-orange-200 hover:border-orange-400 px-2.5 py-1 rounded-full disabled:opacity-50"
                          data-testid={`reset-reschedule-${appt.vertriebler.id}`}>
                          {resettingReschedule === appt.vertriebler.id ? 'Wird zurückgesetzt...' : 'Limit zurücksetzen'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackofficeExamsCalendar;
